import { useEffect, useMemo, useRef, useState } from 'react';

export interface RegionSearchItem {
  id: string;
  label: string;
  kind: 'region' | 'major' | 'minor';
  wx: number;
  wy: number;
}

export interface RegionSearchProps {
  items: ReadonlyArray<RegionSearchItem>;
  onSelect: (item: RegionSearchItem) => void;
}

const KIND_ORDER: Record<RegionSearchItem['kind'], number> = {
  region: 0,
  major: 1,
  minor: 2,
};

const KIND_LABEL: Record<RegionSearchItem['kind'], string> = {
  region: 'Region',
  major: 'Major',
  minor: 'Minor',
};

const KIND_COLOR: Record<RegionSearchItem['kind'], string> = {
  region: 'text-emerald-300',
  major: 'text-amber-300',
  minor: 'text-slate-400',
};

const MAX_RESULTS = 12;

export function RegionSearch({ items, onSelect }: RegionSearchProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as RegionSearchItem[];
    const matches = items.filter((it) => it.label.toLowerCase().includes(q));
    matches.sort((a, b) => {
      const al = a.label.toLowerCase();
      const bl = b.label.toLowerCase();
      const ap = al.startsWith(q) ? 0 : 1;
      const bp = bl.startsWith(q) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      const ak = KIND_ORDER[a.kind];
      const bk = KIND_ORDER[b.kind];
      if (ak !== bk) return ak - bk;
      return al.localeCompare(bl);
    });
    return matches.slice(0, MAX_RESULTS);
  }, [items, query]);

  useEffect(() => {
    setHover(0);
  }, [query]);

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function commit(item: RegionSearchItem): void {
    onSelect(item);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHover((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHover((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (results[hover]) {
        e.preventDefault();
        commit(results[hover]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative w-64">
      <input
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search region or town…"
        className="w-full rounded bg-slate-800 px-2 py-1 text-xs text-slate-200 placeholder:text-slate-500 outline-none focus:ring-1 focus:ring-emerald-500"
      />
      {open && results.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-auto rounded border border-slate-700 bg-slate-900 text-xs shadow-lg">
          {results.map((item, i) => (
            <li
              key={item.id}
              onMouseEnter={() => setHover(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(item);
              }}
              className={`flex cursor-pointer items-center justify-between gap-2 px-2 py-1 ${
                i === hover ? 'bg-slate-800' : ''
              }`}
            >
              <span className="truncate text-slate-200">{item.label}</span>
              <span className={`shrink-0 text-[10px] uppercase ${KIND_COLOR[item.kind]}`}>
                {KIND_LABEL[item.kind]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
